import React, { useState, useEffect } from "react";
import styled from "@emotion/styled";
import {
  isCoreDirect,
  isCorePaid,
  isDE,
  isUS,
  isEnLanguage,
} from "@utils/userSegment";
import { PAID, generateDirectLinks } from "./Links";
import { NavBarNav, NavBarLeft, NavBarItem } from "./Styles";

import { useDisableScroll } from "src/hooks/scroll";
import { trackEvent } from "src/utils/api/tracker";
import { isDirectTraffic } from "src/utils/userSegment/features";
import { FullWidth } from "../core/BodyLayout";
import { Hamburger } from "./Hamburger";
import { Menu } from "./Menu";
import { MenuBackground } from "./MenuBackground";
import { NoomLogo } from "../Logo";
import { getLanguage } from "src/utils/meristemContext";
import { isHolidayPromotion } from "src/utils/holidayPromo";

const getUrls = () => {
  // DE paid-for routes receive the direct nav bar
  if (isDE() && isCorePaid()) {
    return generateDirectLinks();
  }

  return isDirectTraffic() ? generateDirectLinks() : PAID;
};

const NavBarContainer = styled(FullWidth)`
  color: black;
`;
const NavBarLogoLink = styled.a`
  text-decoration: none;
  color: inherit;
`;
const NavBarHamburger = styled(NavBarItem, {
  shouldForwardProp: (prop) => prop !== "usePromoBannerOffset",
})<{ usePromoBannerOffset?: boolean }>`
  border: none;
  ${(props) => props.usePromoBannerOffset && "margin-top: -92px;"}
`.withComponent(Hamburger);

export default function NavBar() {
  const URLS = getUrls();

  // We have a separate variable for language code suffix since wordpress doesn't support /en for some links
  // (i.e. web.noom.com/en/business, so we have to make sure to use web.noom.com/business).
  // This is not the most elegant solution, but will work for now.
  let languageCodeSuffix = "";

  const currentLanguage = getLanguage();

  const externalLinkLanguages = ["de", "es", "jp"];
  if (externalLinkLanguages.indexOf(currentLanguage) >= 0) {
    languageCodeSuffix = `/${currentLanguage}`;
  }

  const [menuOpen, setMenuOpen] = useState(false);

  // If the window is resized, close the menu to prevent any weird states.
  useEffect(() => {
    const resizeWindow = () => setMenuOpen(false);
    window.addEventListener("resize", resizeWindow);
    return () => {
      window.removeEventListener("resize", resizeWindow);
    };
  }, []);

  // Prevent background scrolling while menu is open.
  useDisableScroll(menuOpen);

  const handleClickTopLogo = () => {
    trackEvent("ClickedLandingPageLogo");
  };

  return (
    <NavBarContainer>
      <NavBarNav>
        {/* Disable a11y linter warnings as we don't want users to interact with the logo, just to track their attempts. */}
        {/* eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions */}
        <NavBarLeft onClick={handleClickTopLogo}>
          {/* NOTE(imran): Some routes generate a navbar with no links; in these instances, */}
          {/* provide a method of reaching the direct homepage where links will appear */}
          {URLS && URLS.length ? (
            <NoomLogo css={{ height: 20, paddingRight: 10 }} />
          ) : (
            <NavBarLogoLink href="/ps/landing?route=_direct">
              <NoomLogo css={{ height: 20, paddingRight: 10 }} />
            </NavBarLogoLink>
          )}
        </NavBarLeft>
        {URLS && URLS.length ? (
          <>
            <NavBarHamburger
              active={menuOpen}
              onClick={() => {
                setMenuOpen(!menuOpen);
              }}
              usePromoBannerOffset={isHolidayPromotion() && menuOpen}
            />
            <MenuBackground open={menuOpen} />

            <Menu
              open={menuOpen}
              condensed={isCoreDirect()}
              veryCondensed={isEnLanguage() && isUS() && isCoreDirect()}
            >
              {URLS.map((props) => (
                <props.component
                  languageCodeSuffix={languageCodeSuffix}
                  menuOpen={menuOpen}
                  key={props.id}
                />
              ))}
            </Menu>
          </>
        ) : null}
      </NavBarNav>
    </NavBarContainer>
  );
}
